import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * useAvailabilityStore
 *
 * Person-level "available for redeployment" flag — shared across
 * Redeployment Center (marks it, from the reduction detail screen),
 * Workforce Planning (groups candidates by it), and PAIC Org Redesign's
 * Roles Reduced tab (marks it). One flag, three consumers, matching the
 * leadership-alignment brief's description of a single shared concept.
 * Session-only by design — Talent Mobility data must not live in localStorage.
 */
const useAvailabilityStore = create(
  devtools(
    (set, get) => ({
      // keyed by personId -> { markedAvailableAt, markedFrom }
      available: {},

      markAvailable: (personId, source) =>
        set((state) => ({
          available: {
            ...state.available,
            [personId]: { markedAvailableAt: Date.now(), markedFrom: source },
          },
        })),

      markUnavailable: (personId) =>
        set((state) => {
          const next = { ...state.available };
          delete next[personId];
          return { available: next };
        }),

      isAvailable: (personId) => Boolean(get().available[personId]),
    }),
    { name: 'AvailabilityStore' }
  )
);

export const useIsAvailable = (personId) => {
  const entry = useAvailabilityStore((state) => state.available[personId]);
  return useMemo(() => Boolean(entry), [entry]);
};

export default useAvailabilityStore;
